import { useMemo } from 'react';

import {
  REST_FALLBACK_SEC,
  estimateLiveWorkoutCalories,
  resolveAthleteEnergy,
  resolveWorkoutCalories,
  type EffortScale,
  type MovementPattern,
  type ResolvedWorkoutCalories,
} from '@jackedlogs/shared';

import type { LoggerDraft } from './state/loggerDraftTypes';
import type { LoggerProfile } from './hostDeps';
import { buildPayload } from './workoutPayload';

// HC-8 — the LIVE workout energy readout for the logger footer. There's no
// endedAt mid-workout, so estimateLiveWorkoutCalories PROJECTS the duration from
// the set structure (reps + rest) and reuses the save-path estimator; it
// re-projects as sets/exercises change. Only the estimate exists live (HC
// enriches the measured value later), so the resolver just wraps it — the
// measured-vs-estimate case is a saved, enriched session (B2c), handled by
// useWorkoutEnergyReadout, not this live path. `hcWins` is passed in (read once
// by the caller) rather than subscribed here, so the two readout paths don't
// each register their own preference listener.
export function useLiveWorkoutEnergy(
  draft: LoggerDraft,
  effortScale: EffortScale,
  profile: LoggerProfile,
  hcWins: boolean,
): ResolvedWorkoutCalories | null {
  return useMemo<ResolvedWorkoutCalories | null>(() => {
    const built = buildPayload(draft, effortScale, { dropUncompleted: false });
    if (!built.ok) return null;
    const patterns: Record<string, MovementPattern | null> = {};
    for (const e of draft.entries) patterns[e.exerciseId] = e.movementPattern;
    const estimate = estimateLiveWorkoutCalories(
      built.payload,
      patterns,
      resolveAthleteEnergy(profile),
      profile.defaultRestSec ?? REST_FALLBACK_SEC,
    );
    return estimate
      ? resolveWorkoutCalories({ caloriesEstimate: estimate }, { hcWins })
      : null;
  }, [draft, effortScale, profile, hcWins]);
}
